'use client';

import Image from 'next/image';
import {
  Activity, Calendar, Camera, Check, CheckCircle2, Clock, CreditCard, Lock, MapPin, QrCode,
  Search, Trophy, Wallet,
} from 'lucide-react';

import { useIsMobile } from '@/hooks/use-mobile';

import { W, FONT } from './tokens';

/**
 * The product surfaces shown on the feature cards, drawn rather than screenshotted.
 *
 * WHY COMPONENTS AND NOT CAPTURES. A capture is a fixed pixel grid: it clips
 * badly at card widths that range from 348px to 788px, it goes soft on a retina
 * screen, it carries whatever data happened to be in the database that morning
 * (one of them shipped a real organiser's postal address), and it silently rots
 * the moment the product moves. These are laid out fluidly, stay sharp at any
 * size, and contain only invented people and events.
 *
 * THEY ARE PORTRAITS, NOT THE REAL THING. Nothing here fetches, routes or
 * responds to a click. Anything interactive would be a promise the marketing
 * page cannot keep. They copy the redesigned dashboard's visual language
 * (see components/design/) closely enough to be recognisable and are not
 * imported from it, because the product's components carry state, data
 * requirements and behaviour that have no business on a public page.
 *
 * Every surface is fluid down to ~340px. The cards they sit in vary by span,
 * so a fixed width would either clip mid-table or leave a gutter.
 */

const SURFACE: React.CSSProperties = {
  fontFamily: FONT,
  color: W.ink,
  background: '#FFFFFF',
  // A MINIMUM. At height:100% the surface was squeezed into whatever the frame
  // gave it, which crushed the draw's match boxes until the second name in each
  // was half cut off. It fills the frame and, where the frame is smaller, runs
  // past the bottom edge, which is what these frames crop by design.
  minHeight: '100%',
  padding: '18px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  overflow: 'hidden',
};

const SURFACE_MOBILE: React.CSSProperties = { ...SURFACE, padding: '14px 15px', gap: 9 };

/** Every surface is tighter on a phone, which buys back a row's worth of height. */
function surfaceStyle(isMobile: boolean): React.CSSProperties {
  return isMobile ? SURFACE_MOBILE : SURFACE;
}

const RULE = `1px solid ${W.rule}`;

function Title({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>{children}</div>
      {sub && <div style={{ fontSize: 13, color: W.body, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** The stat tiles that head most dashboard sections. */
function Stats({ items }: { items: Array<[string, string, string?]> }) {
  const isMobile = useIsMobile();
  const shown = isMobile ? items.slice(0, 2) : items;
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`, gap: 8 }}
    >
      {shown.map(([label, value, tone]) => (
        <div
          key={label}
          style={{
            border: `1px solid ${tone === 'warn' ? '#EBD9B4' : W.rule}`,
            borderRadius: 8,
            padding: '8px 10px',
            background: tone === 'warn' ? '#FDF6EA' : '#FFFFFF',
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: 11, color: W.mute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2, color: tone === 'warn' ? '#9A6412' : W.ink }}>
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'live' }) {
  const tones = {
    neutral: { bg: '#EFF3F2', fg: W.body },
    good: { bg: '#E4F1EC', fg: '#1F6B54' },
    warn: { bg: '#FBEFD9', fg: '#8A5B10' },
    live: { bg: W.teal, fg: '#FFFFFF' },
  }[tone];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11.5, fontWeight: 500, padding: '3px 8px', borderRadius: 5,
        background: tones.bg, color: tones.fg, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function Avatar({ initials }: { initials: string }) {
  return (
    <span
      style={{
        width: 26, height: 26, flex: 'none', borderRadius: 6, background: '#EFF3F2',
        fontSize: 10.5, fontWeight: 700, color: W.body,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {initials}
    </span>
  );
}

function Row({ children, last, k }: { children: React.ReactNode; last?: boolean; k?: number }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
        borderBottom: last ? 'none' : RULE, minWidth: 0,
        ...(k === undefined
          ? null
          : { animation: `fvLine 620ms cubic-bezier(0.22,1,0.36,1) ${(k * 0.08).toFixed(2)}s both` }),
      }}
    >
      {children}
    </div>
  );
}

function Grow({ title, note }: { title: string; note: string }) {
  return (
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {title}
      </span>
      <span style={{ display: 'block', fontSize: 12.5, color: W.body, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {note}
      </span>
    </span>
  );
}

function Button({ children, primary }: { children: React.ReactNode; primary?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 7, whiteSpace: 'nowrap',
        background: primary ? W.teal : '#FFFFFF',
        color: primary ? '#FFFFFF' : W.ink,
        border: primary ? 'none' : `1px solid ${W.borderStrong}`,
      }}
    >
      {children}
    </span>
  );
}

/* ── 1. Branded event pages, built to the REAL public page design ──────── */

/**
 * The organiser's public event page, as the product actually renders it.
 *
 * Rebuilt from apps/web/components/public/v2/V2EventHero.tsx and EventPageBody
 * rather than sketched: badge row (a pulsing Live dot, the activity type with
 * its icon, the event nature), title in the brand's heading font, meta rows
 * each with their own icon, the overlapping avatar stack with "+N more
 * registered", cover, an about block, then the sticky price bar with Get
 * tickets. An earlier version of this card invented a simpler layout, which
 * undersold the thing the card is about: these are real pages.
 *
 * Light and dark, because the real theming supports both and an organiser
 * picking a dark skin is the clearest possible proof the page is theirs.
 * Every brand, event and figure below is invented.
 */
type BrandPage = {
  brand: string;
  /** Their real mark from the brand pack, not a text stand-in. */
  logo: string;
  logoHeight: number;
  logoWidth: number;
  /** Each brand picks its own heading typeface. */
  headingFont: string;
  headingCase: 'uppercase' | 'none';
  headingSize: number;
  domain: string;
  accent: string;
  /** Text rendered ON the accent. Some accents need black, not white. */
  onAccent: string;
  dark: boolean;
  event: string;
  date: string;
  time: string;
  where: string;
  activity: string;
  nature: string;
  registered: number;
  initials: string[];
  price: string;
  about: string;
  img: string;
  /** The page ground, and the lighter wash behind the header. */
  ground: string;
  groundTop: string;
  /** White-on-black artwork needs a dark tile behind it on a light page. */
  logoOnDark?: boolean;
  /** Full-colour artwork that must not be knocked out to white on a dark page. */
  keepLogoColour?: boolean;
  /** Which part of the shared map this venue sits in. */
  mapPan: number;
  /** The full venue line, shown in the Event Location card. */
  address: string;
};

const PAGES: BrandPage[] = [
  {
    brand: 'est.28', logo: '/website/brand/logo-est28-trim.png', logoHeight: 13, logoWidth: 62,
    domain: 'est28.co.uk', accent: '#E8B33D', onAccent: '#1B1200', dark: true,
    ground: '#141F33', groundTop: '#1E2C45', mapPan: 0,
    headingFont: 'var(--font-barlow-condensed)', headingCase: 'uppercase', headingSize: 19,
    event: 'EST.28 TRYKA SIMULATION', date: 'Saturday 12 September 2026', time: '8:30 A.M.',
    where: 'est.28, Bessbrook, Newry', address: 'est.28, Unit 2 Crabtree Hill, Bessbrook, Newry',
    activity: 'TRYKA', nature: 'Competitive',
    registered: 69, initials: ['DM', 'CK', 'RB'], price: 'From £80',
    about: 'Eight 800m runs paired with eight functional stations, judged against the clock. Singles, pairs and relay divisions racing across two floors from 8:30, with doors and warm-up from 7:45.',
    img: '/website/uploads/venue-evolve.jpg',
  },
  {
    // Strive's live page: the "bold" font style (Bebas Neue headings), their
    // cyan with dark text on it, Forge's own dark ground. Their mark is
    // white-and-cyan artwork, shown as it is, not knocked out.
    brand: 'Strive Fitness', logo: '/website/brand/logo-strive.png', logoHeight: 22, logoWidth: 22,
    domain: 'strivefitness.ie', accent: '#00AACB', onAccent: '#00201E', dark: true,
    ground: '#0F0E0C', groundTop: '#16211D', mapPan: -104, keepLogoColour: true,
    headingFont: 'var(--font-bebas-neue)', headingCase: 'none', headingSize: 19,
    event: 'Strive Fitness Simulation', date: 'Saturday 26 June 2027', time: '8:00 A.M.',
    where: 'Kilcock, Co. Kildare', address: 'North Kildare Multi Sports Club, Kilcock, Co. Kildare',
    activity: 'HYROX', nature: 'Casual Race',
    registered: 144, initials: ['AN', 'PK', 'MO'], price: 'From €45',
    about: 'A full simulation at race weights on the floor at Strive Kildare. Eight stations, eight runs, judged the way the real thing is judged, with heats seeded on your entry time.',
    img: '/website/uploads/venue-strive.jpg',
  },
  {
    // DQ is a functional-fitness coach, not a trail-race organiser, and their
    // mark is white-on-black artwork that vanished on a light page.
    brand: 'DQ Coaching', logo: '/website/brand/logo-dq.png', logoHeight: 20, logoWidth: 20,
    logoOnDark: true,
    domain: 'dqcoaching.ie', accent: '#E2540C', onAccent: '#FFFFFF', dark: false,
    ground: '#FFFFFF', groundTop: '#FFF6F1', mapPan: 104,
    headingFont: 'var(--font-instrument-sans)', headingCase: 'none', headingSize: 16,
    event: 'DQ HYROX Simulation', date: 'Saturday 26 June 2027', time: '9:00 A.M.',
    where: 'Navan, Co. Meath', address: 'DQ Coaching, Navan, Co. Meath',
    activity: 'HYROX', nature: 'Casual Race',
    registered: 183, initials: ['DC', 'CM', 'SK'], price: 'From €45',
    about: 'A full race simulation at HYROX weights. Eight functional stations paired with eight 1km runs, singles and doubles, judged on the floor. Open division, so bring whatever fitness you turn up with.',
    img: '/website/uploads/grace-anne-bobadilla-z2ltNyVDsNY-unsplash.jpg',
  },
];

/**
 * A stylised course map. The About block on a real page is short and the space
 * under it looked empty, and a map is what an organiser actually puts there.
 * Drawn rather than screenshotted so it takes the brand's accent.
 */
function MiniMap({ accent, dark, pan }: { accent: string; dark: boolean; pan: number }) {
  // The real page embeds a Google map with a pin on it, so this reads as one:
  // pale land, white roads with a grey casing, a park, water, and a teardrop
  // pin at the venue. An earlier version was an abstract grid with a route
  // line through it, which looked like a chart, not a map.
  const land = dark ? '#2A2E33' : '#F2EFE9';
  const roadCasing = dark ? '#3A3F45' : '#E4DFD6';
  const road = dark ? '#4A5057' : '#FFFFFF';
  const park = dark ? '#2C3A2E' : '#CDE3C4';
  const water = dark ? '#26333F' : '#A9D2EE';
  const label = dark ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.20)';

  return (
    // `pan` slides the viewBox so the three phones show different parts of the
    // same city rather than three copies of one tile.
    <svg viewBox={`${pan} 14 200 50`} style={{ display: 'block', width: '100%', height: 'auto' }} aria-hidden>
      <rect x="-120" width="440" height="78" fill={land} />
      <path d="M-40 0 h94 v30 h-94 z" fill={park} />
      <path d="M228 0 h52 v22 h-52 z" fill={park} />
      <path d="M150 78 L200 78 L200 40 C 182 48, 168 62, 150 78 Z" fill={water} />
      <path d="M-120 12 L-60 12 L-60 -6 L-120 -6 Z" fill={water} />

      {/* Roads: a wide casing under a narrower fill, the way map tiles draw them. */}
      {[
        'M-124 34 H324',
        'M-124 62 H324',
        'M62 -4 V82',
        'M132 -4 V82',
        'M-16 -4 V82',
        'M212 -4 V82',
        'M62 34 L132 62',
        'M-16 62 L62 34',
      ].map((d, i) => (
        <g key={d}>
          <path d={d} stroke={roadCasing} strokeWidth={i < 2 ? 9 : 7} fill="none" strokeLinecap="round" />
          <path d={d} stroke={road} strokeWidth={i < 2 ? 6 : 4} fill="none" strokeLinecap="round" />
        </g>
      ))}

      <text x="-32" y="20" fontSize="6" fill={label} fontFamily="sans-serif">Riverside Park</text>
      <text x="150" y="30" fontSize="5.5" fill={label} fontFamily="sans-serif">Quay St</text>
      <text x="234" y="16" fontSize="5.5" fill={label} fontFamily="sans-serif">The Green</text>

      {/* The pin, in the organiser's accent, at the centre of whatever is panned
          into view. */}
      <g transform={`translate(${pan + 100} 30)`}>
        <ellipse cx="0" cy="20" rx="5" ry="2" fill="rgba(0,0,0,0.20)" />
        <path
          d="M0 20 C 0 20, -8 10, -8 4 A 8 8 0 1 1 8 4 C 8 10, 0 20, 0 20 Z"
          fill={accent}
          stroke={dark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.85)'}
          strokeWidth="1.2"
        />
        <circle cx="0" cy="4" r="2.8" fill={dark ? '#141414' : '#FFFFFF'} />
      </g>
    </svg>
  );
}

function PhonePage({ page, raised, order }: { page: BrandPage; raised: boolean; order: number }) {
  // Inline, not a media query. Appending to globals.css silently dropped the
  // last block in the file more than once, and CLAUDE.md already names this
  // hook as the way to branch layout by width in this codebase.
  const isMobile = useIsMobile();
  const ink = page.dark ? '#F7F5F2' : '#171412';
  const muted = page.dark ? 'rgba(247,245,242,0.62)' : 'rgba(23,20,18,0.58)';
  const line = page.dark ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.09)';
  const raisedGround = page.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';

  // The middle phone stands proud of the other two. marginBottom cancels the
  // lift so all three still sit on the card's bottom edge.
  return (
    <div
      className="fvw-phone"
      style={{
        ['--fvw-lift' as string]: raised ? '-26px' : '0px',
        marginBottom: raised ? -26 : 0,
        zIndex: raised ? 2 : 1,
        order,
      }}
    >
      <div
        style={{
          position: 'relative',
          background: page.ground,
          color: ink, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', fontFamily: FONT,
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: [
              `radial-gradient(ellipse 70% 22% at 15% 0%, ${page.accent}${page.dark ? '4D' : '38'} 0%, transparent 75%)`,
              `radial-gradient(ellipse 50% 15% at 80% 3%, ${page.accent}${page.dark ? '26' : '1F'} 0%, transparent 60%)`,
              `radial-gradient(ellipse 40% 12% at 50% 26%, ${page.accent}${page.dark ? '14' : '12'} 0%, transparent 70%)`,
            ].join(', '),
          }}
        />
        <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Their real mark, and "Powered by Fitvent" opposite. That line is the
            only place our name appears on an organiser's page. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, flex: 'none', padding: '0 12px', borderBottom: `1px solid ${line}` }}>
          {/* Some marks are white-on-black artwork. On a light page they
              disappear entirely, which is what happened to DQ's, so those sit
              on a dark tile exactly as the real page renders them. */}
          <span
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 26, flex: 'none',
              ...(page.logoOnDark ? { background: '#0B0B0B', borderRadius: 5, padding: '3px 6px' } : null),
            }}
          >
            <Image
              src={page.logo}
              alt={page.brand}
              width={page.logoWidth * 4}
              height={page.logoHeight * 4}
              style={{
                height: page.logoHeight, width: 'auto', objectFit: 'contain',
                // Dark-on-transparent marks knock out to white on a dark ground.
                ...(page.dark && !page.logoOnDark && !page.keepLogoColour ? { filter: 'brightness(0) invert(1)' } : null),
              }}
            />
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 6.5, color: muted, whiteSpace: 'nowrap' }}>Powered by Fitvent</span>
        </div>

        <div style={{ padding: '11px 12px 0' }}>
          {/* Pills wrap as whole pills, as the real page's flex-wrap row does,
              rather than breaking "Casual Race" inside its own border. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, minHeight: 20, marginBottom: 7 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 7, fontWeight: 500, padding: '3px 7px', borderRadius: 99, background: 'rgba(34,197,94,0.10)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.38)' }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#22C55E' }} />
              Live
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 7, fontWeight: 500, padding: '3px 7px', borderRadius: 99, background: raisedGround, color: muted, border: `1px solid ${line}` }}>
              {page.activity === 'HYROX' ? <Trophy size={7} /> : <Activity size={7} />}
              {page.activity}
            </span>
            <span style={{ fontSize: 7, fontWeight: 500, padding: '3px 7px', borderRadius: 99, background: raisedGround, color: muted, border: `1px solid ${line}`, whiteSpace: 'nowrap' }}>
              {page.nature}
            </span>
          </div>

          {/* Each brand sets its own heading typeface. Three pages in one
              typeface would undercut the card's whole claim: the fonts are
              part of what an organiser gets to choose. */}
          <div
            style={{
              fontFamily: `${page.headingFont}, ${FONT}`,
              fontSize: page.headingSize, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.015em',
              textTransform: page.headingCase, marginBottom: 9,
              // The whole title, wrapping as the real page wraps it. It used to
              // be clipped to one line with an ellipsis, which cut every long
              // event name mid-word on the narrow single phone on mobile.
              minHeight: 20, display: 'flex', alignItems: 'center',
            }}
          >
            {page.event}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: '3px 10px', minHeight: 28, marginBottom: 7 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 7.5, color: muted, whiteSpace: 'nowrap' }}>
              <Calendar size={8} color={page.accent} />
              {page.date}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 7.5, color: muted, whiteSpace: 'nowrap' }}>
              <Clock size={8} color={page.accent} />
              {page.time}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 7.5, color: muted, width: '100%', minWidth: 0 }}>
              <MapPin size={8} color={page.accent} style={{ flex: 'none' }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.where}</span>
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 17, marginBottom: 8 }}>
            <span style={{ display: 'flex' }}>
              {page.initials.map((init, i) => (
                <span
                  key={init}
                  style={{
                    width: 18, height: 18, borderRadius: '50%', background: page.accent, color: page.onAccent,
                    fontSize: 6.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginLeft: i === 0 ? 0 : -6, boxShadow: `0 0 0 1.5px ${page.ground}`,
                    filter: `hue-rotate(${[0, 40, -30][i]}deg)`,
                  }}
                >
                  {init}
                </span>
              ))}
            </span>
            <span style={{ fontSize: 7.5, color: muted }}>+ {page.registered} more registered</span>
          </div>
        </div>

        <div
          style={{
            position: 'relative', height: 92, margin: '0 12px', borderRadius: 8,
            overflow: 'hidden', border: `1px solid ${line}`, flex: 'none',
          }}
        >
          <Image src={page.img} alt="" fill sizes="240px" style={{ objectFit: 'cover' }} />
        </div>

        {/* About and the map are SEPARATE sections on the real page. Nesting
            the map inside the About card made it look like an illustration of
            the copy rather than the venue. */}
        <div style={{ margin: '8px 12px 0', border: `1px solid ${line}`, borderRadius: 8, padding: '7px 10px 8px', flex: 'none' }}>
          <div style={{ fontFamily: `${page.headingFont}, ${FONT}`, fontSize: 10, fontWeight: 700, height: 13, display: 'flex', alignItems: 'center', marginBottom: 3 }}>
            About This Event
          </div>
          {/* Three lines in every phone, so the card below starts at the same
              height in all three. */}
          <div
            style={{
              fontSize: 7.5, color: muted, lineHeight: 1.6,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {page.about}
          </div>
        </div>

        {/* Dropped on a phone, where this one card is the difference between a
            device that fits the screen and one that sets the height for all
            eight cards. The map is a nice-to-have; the entry flow is not. */}
        {!isMobile && (
        <div style={{ margin: '8px 12px 0', border: `1px solid ${line}`, borderRadius: 8, padding: '7px 10px 8px', flex: 'none' }}>
          <div style={{ fontFamily: `${page.headingFont}, ${FONT}`, fontSize: 10, fontWeight: 700, height: 13, display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            Event Location
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 7.5, color: muted, marginBottom: 6 }}>
            <MapPin size={8} color={page.accent} style={{ flex: 'none' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.address}</span>
          </div>
          <div style={{ borderRadius: 6, overflow: 'hidden' }}>
            <MiniMap accent={page.accent} dark={page.dark} pan={page.mapPan} />
          </div>
        </div>
        )}

        {/* The sticky sheet: drag handle, name in letterspaced caps, price, and
            a full-width pill CTA in the organiser's colour. */}
        <div
          style={{
            marginTop: 'auto', padding: '6px 12px 10px', borderRadius: '12px 12px 0 0',
            background: page.ground, borderTop: `1px solid ${line}`, position: 'relative',
            boxShadow: page.dark
              ? '0 -10px 22px rgba(0,0,0,0.55)'
              : '0 -10px 22px rgba(16,32,29,0.13)',
          }}
        >
          <span style={{ display: 'block', width: 26, height: 3, borderRadius: 99, background: line, margin: '0 auto 7px' }} />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 6.5, letterSpacing: '0.07em', color: muted, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {page.event}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{page.price}</span>
          </div>
          <span
            style={{
              display: 'block', textAlign: 'center', fontSize: 9, fontWeight: 700,
              color: page.onAccent, background: page.accent, padding: '7px 0', borderRadius: 99,
            }}
          >
            Get Tickets
          </span>
        </div>
        </span>
      </div>
    </div>
  );
}

/**
 * Desktop shows all three, Strive raised in the middle. A phone shows only the
 * FIRST child (globals.css), so Strive leads the markup: the home hero already
 * walks through est.28, and this card's one example should be someone else.
 * `order` puts them back est.28, Strive, DQ on screen.
 */
const PHONE_ORDER = [
  { page: PAGES[1], order: 2, raised: true },
  { page: PAGES[0], order: 1, raised: false },
  { page: PAGES[2], order: 3, raised: false },
];

export function BrandedPagesSurface() {
  return (
    <div className="fvw-phone-row">
      {PHONE_ORDER.map(({ page, order, raised }) => (
        <PhonePage key={page.brand} page={page} raised={raised} order={order} />
      ))}
    </div>
  );
}

/* ── 2. Split payment ──────────────────────────────────────────────────── */

export function SplitPaymentSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={{ ...SURFACE, background: '#152036', color: '#FFFFFF' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.66)' }}>
        <span>Step 2 of 5</span>
        <span style={{ fontWeight: 600, color: '#FFFFFF' }}>Payment</span>
      </div>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            style={{
              height: 4, borderRadius: 99, flex: i === 1 ? 2 : 1,
              background: i === 0 ? '#3FBF8F' : i === 1 ? '#E8A33D' : 'rgba(255,255,255,0.18)',
            }}
          />
        ))}
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', marginTop: 2 }}>
        Autumn Throwdown 2026
      </div>

      {[
        { label: 'I’ll pay for everyone', note: 'Pay €85.20 now, for all 2 members', on: false },
        {
      label: 'Split between members',
      note: isMobile
        ? 'You authorise €85.20 as a hold. Each teammate pays their €42.60 share.'
        : 'You authorise €85.20 as a hold. Each teammate pays their €42.60 share, and you are only charged for whatever is unpaid after the deadline.',
      on: true,
    },
      ].map((o) => (
        <div
          key={o.label}
          style={{
            display: 'flex', gap: 10, padding: '11px 12px', borderRadius: 9,
            border: `1px solid ${o.on ? '#E8A33D' : 'rgba(255,255,255,0.14)'}`,
            background: o.on ? 'rgba(232,163,61,0.09)' : 'transparent',
          }}
        >
          <span
            style={{
              width: 15, height: 15, flex: 'none', marginTop: 2, borderRadius: '50%',
              border: `2px solid ${o.on ? '#E8A33D' : 'rgba(255,255,255,0.34)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {o.on && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E8A33D' }} />}
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{o.label}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.66)', lineHeight: 1.5, marginTop: 2 }}>
              {o.note}
            </span>
          </span>
        </div>
      ))}

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <CreditCard size={14} color="rgba(255,255,255,0.7)" style={{ flex: 'none' }} />
        <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.78)' }}>
          Card, Apple Pay and Google Pay
        </span>
        <span
          style={{
            marginLeft: 'auto', fontSize: 10.5, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap',
          }}
        >
          Payments by Stripe
        </span>
      </div>
    </div>
  );
}

/* ── 3. Waves, heats and scoring ───────────────────────────────────────── */

const WAVES = [
  { name: 'Wave 1 · Elite', time: '09:00', filled: 48, cap: 50 },
  { name: 'Wave 2 · Open', time: '09:20', filled: 50, cap: 50 },
  { name: 'Wave 3 · Open', time: '09:40', filled: 37, cap: 50 },
  { name: 'Wave 4 · Doubles', time: '10:00', filled: 22, cap: 40 },
  { name: 'Wave 5 · Relay', time: '10:20', filled: 12, cap: 40 },
];

export function WavesSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Title sub="Built from 169 entries across 4 divisions">Start waves</Title>
        <span style={{ marginLeft: 'auto', flex: 'none' }}>
          <Button primary>Publish schedule</Button>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Pill tone="good">Generated</Pill>
        <Pill>20 min apart</Pill>
        <Pill>Capacity 50</Pill>
        <Pill>Athletes may self-select</Pill>
      </div>

      <div style={{ minWidth: 0 }}>
        {WAVES.map((w, i) => (
          <Row key={w.name} last={i === WAVES.length - 1} k={i}>
            <span
              style={{
                fontSize: 12.5, fontWeight: 600, flex: 'none', width: 46,
                fontVariantNumeric: 'tabular-nums', color: W.body,
              }}
            >
              {w.time}
            </span>
            <Grow title={w.name} note={`${w.filled} of ${w.cap} places taken`} />
            <span style={{ flex: 'none', width: 78 }}>
              <span style={{ display: 'block', height: 3, borderRadius: 99, background: '#E3EAE8' }}>
                <span
                  style={{
                    display: 'block', height: '100%', borderRadius: 99,
                    width: `${(w.filled / w.cap) * 100}%`,
                    background: w.filled === w.cap ? '#C08A2E' : W.teal,
                  }}
                />
              </span>
            </span>
            <span style={{ flex: 'none' }}>
              {w.filled === w.cap ? <Pill tone="warn">Full</Pill> : <Pill tone="good">Open</Pill>}
            </span>
          </Row>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: W.mute, marginTop: 'auto' }}>
        Publishing emails every athlete their wave and start time, in your branding.
      </div>
    </div>
  );
}


/* ── 3b. The draw bracket, and the real format picker ──────────────────── */

/**
 * An eight-entrant knockout, as the design shows it on the waves card.
 *
 * The earlier version of this card showed the start-waves table, which is a
 * fine surface but says "list" where the card's claim is "draws". A bracket
 * says it in one glance: pairs resolve into a round, rounds resolve into a
 * winner, and nobody had to draw it by hand.
 */
const R1: Array<[string, string]> = [
  ['Byrne', 'Nolan'],
  ['Doyle', 'Walsh'],
  ['Kelly', 'Ryan'],
  ['Fox', 'Hayes'],
];

function Match({ names, delay, dim }: { names: string[]; delay: number; dim?: boolean }) {
  return (
    <div
      style={{
        border: RULE, borderRadius: 7, overflow: 'hidden', background: '#FFFFFF',
        animation: `fvSeed 3.4s ease-out ${delay}s both`,
      }}
    >
      {names.map((n, i) => (
        <div
          key={n}
          style={{
            fontSize: 12, fontWeight: i === 0 ? 600 : 400,
            color: i === 0 ? W.ink : (dim ? W.mute : W.body),
            padding: '5px 9px',
            borderTop: i === 0 ? 'none' : RULE,
            background: i === 0 ? '#FFFFFF' : '#F7FAF9',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {n}
        </div>
      ))}
    </div>
  );
}

/** The right-hand bracket that joins a pair of matches into the next round. */
function Connector({ delay, height }: { delay: number; height: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: 12, height,
        borderTop: RULE, borderRight: RULE, borderBottom: RULE,
        borderRadius: '0 6px 6px 0',
        animation: `fvSeed 3.4s ease-out ${delay}s both`,
      }}
    />
  );
}

export function BracketSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Title sub="Seeded from 64 entered pairs, byes placed">Round of 8</Title>
      </div>

      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flex: 1, minHeight: 0, paddingTop: 4 }}>
        <div style={{ display: 'grid', gap: 10, flex: 1, minWidth: 0, alignContent: 'space-between' }}>
          {R1.map((m, k) => (
            <Match key={m[0]} names={m} delay={k * 0.16} />
          ))}
        </div>

        <div style={{ display: 'grid', alignContent: 'space-around', flex: 'none', paddingLeft: 6 }}>
          <Connector delay={0.6} height={58} />
          <Connector delay={0.72} height={58} />
        </div>

        <div style={{ display: 'grid', gap: 10, flex: 1, minWidth: 0, alignContent: 'space-around', paddingLeft: 6 }}>
          <Match names={['Byrne', 'Doyle']} delay={0.84} dim />
          <Match names={['Kelly', 'Fox']} delay={0.96} dim />
        </div>

        <div className="fvw-bracket-final" style={{ display: 'grid', alignContent: 'center', flex: 'none', paddingLeft: 6 }}>
          <Connector delay={1.08} height={92} />
        </div>

        <div className="fvw-bracket-final" style={{ display: 'grid', alignContent: 'center', flex: 'none', paddingLeft: 6, width: 78 }}>
          <div
            style={{
              border: `1px solid ${W.teal}`, borderRadius: 7, background: '#EAF3F1',
              padding: '6px 9px', fontSize: 12, fontWeight: 700, color: W.teal,
              animation: 'fvTick 3.4s ease-out 1.2s both',
            }}
          >
            Byrne
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The new-event format picker, as the wizard actually presents it.
 *
 * These are the templates that really ship (apps/web/lib/wizard/templates.ts).
 * Do not add a row here for a format the wizard cannot create: naming one on
 * the marketing site and not having it in the product is the single most
 * annoying thing this page could do to an organiser.
 */
const FORMATS: Array<[string, string]> = [
  ['Running race', 'Chip timed road or park race with results.'],
  ['Trail run', 'Off road, through trails or hills.'],
  ['Triathlon', 'Swim, bike, run. Sprint or olympic.'],
  ['Obstacle course race', 'Mud and obstacles, competitive and open waves.'],
  ['Fun run', 'Casual, untimed, all ages welcome.'],
  ['Social run', 'A run with coffee, brunch or a pint after.'],
];

export function FormatPickerSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: W.mute }}>
        {['Template', 'Basics', 'Format', 'Tickets', 'Page', 'Publish'].map((step, i) => (
          <span key={step} style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <span
              style={{
                width: 15, height: 15, borderRadius: '50%', fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: i === 0 ? W.teal : '#EFF3F2', color: i === 0 ? '#fff' : W.mute,
              }}
            >
              {i + 1}
            </span>
            <span style={{ color: i === 0 ? W.ink : W.mute, fontWeight: i === 0 ? 600 : 400 }}>{step}</span>
          </span>
        ))}
      </div>

      <Title sub="Pick a format to carry on. Everything below is set up for you.">What are you running?</Title>

      <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', color: W.mute }}>ENDURANCE</div>
      <div style={{ minWidth: 0 }}>
        {FORMATS.map(([name, note], i) => (
          <Row key={name} last={i === FORMATS.length - 1} k={i}>
            <Grow title={name} note={note} />
            <span style={{ flex: 'none', color: W.mute, fontSize: 13 }}>›</span>
          </Row>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: W.mute, marginTop: 'auto' }}>
        Plus HYROX, TRYKA, Deadly Dozen, DEKA, CrossFit and Hybrid under functional fitness.
      </div>
    </div>
  );
}

/* ── 4. Check-in ───────────────────────────────────────────────────────── */

const DESK = [
  { initials: 'MK', name: 'Maria Kelleher', note: 'Wave 1 at 09:00 · with Aoife Rice', state: 'ready' },
  { initials: 'SD', name: 'Sinead Doran', note: 'No waiver signed yet', state: 'blocked' },
  { initials: 'EM', name: 'Emmett Murtagh', note: 'Checked in at 07:42', state: 'done' },
  { initials: 'CB', name: 'Cathal Byrne', note: 'Wave 2 at 09:20', state: 'ready' },
];

export function CheckInSurface() {
  const isMobile = useIsMobile();
  const desk = isMobile ? DESK.slice(0, 2) : DESK;
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <Title sub="Built for a phone at the desk. Scan a ticket, or type a name.">Check-in</Title>

      <Stats items={[['Checked in', '3'], ['Still to arrive', '59'], ['Needs a waiver', '62', 'warn'], ['First wave', '09:00']]} />

      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: W.teal, color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '9px 0', borderRadius: 7 }}>
            <QrCode size={14} /> Scan a ticket
          </span>
        </span>
      </div>
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, border: RULE, borderRadius: 7, padding: '7px 10px', color: W.mute, fontSize: 12.5 }}>
          <Search size={13} /> Or search a name
        </div>
      )}

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', color: W.mute, paddingBottom: 4 }}>
          AT THE DESK NOW
        </div>
        {desk.map((d, i) => (
          <Row key={d.name} last={i === desk.length - 1} k={i}>
            <Avatar initials={d.initials} />
            <Grow title={d.name} note={d.note} />
            <span style={{ flex: 'none' }}>
              {d.state === 'done' ? (
                <Pill tone="good">
                  <CheckCircle2 size={11} strokeWidth={2.2} style={{ flex: 'none' }} />
                  Done
                </Pill>
              ) : d.state === 'blocked' ? (
                <Button>Sign here</Button>
              ) : (
                <Button primary>Check in</Button>
              )}
            </span>
          </Row>
        ))}
      </div>

      {!isMobile && (
        <div style={{ fontSize: 11.5, color: W.mute, marginTop: 'auto' }}>
          Works without signal. Anything you scan offline syncs when you are back online.
        </div>
      )}
    </div>
  );
}

/* ── 5. Photos and add-ons in the basket ───────────────────────────────── */

export function AddOnsSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <Title sub="One basket, one payment">Your entry</Title>

      <div style={{ minWidth: 0 }}>
        <Row k={0} last={isMobile}>
          <Grow title="Individual entry · Rx" note="Autumn Throwdown 2026" />
          <span style={{ fontSize: 13, fontWeight: 600 }}>£55.00</span>
        </Row>
        {/* The merch line goes on a phone. The entry plus the add-on is what
            "the same basket" means; a second add-on repeats the point, and it
            was the row pushing the total off the bottom edge. */}
        {!isMobile && (
          <Row k={1}>
            <Grow title="Event t-shirt · M" note="Collect on the day" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>£22.00</span>
          </Row>
        )}
      </div>

      {/* The add-on being chosen, raised out of the list. */}
      <div style={{ border: `1px solid ${W.teal}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ position: 'relative', height: isMobile ? 62 : 96, background: '#EAF1EF' }}>
          <Image src="/website/uploads/felix-yu-ZlwCocz11D8-unsplash.jpg" alt="" fill sizes="360px" style={{ objectFit: 'cover' }} />
          <span style={{ position: 'absolute', top: 8, left: 8, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.94)' }}>
            <Camera size={11} /> Race photos
          </span>
        </div>
        <div style={{ padding: isMobile ? '8px 10px 10px' : '10px 12px 12px' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Every photo of you</div>
          <div
            style={{
              fontSize: 12.5, color: W.body, margin: isMobile ? '2px 0 8px' : '2px 0 10px',
              ...(isMobile
                ? { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                : null),
            }}
          >
            Found by face, delivered the evening of the race.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: W.teal }}>£12.00</span>
            <span style={{ marginLeft: 'auto' }}>
              <Button primary>
                <Check size={13} /> Added
              </Button>
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto', paddingTop: 10, borderTop: RULE }}>
        <span style={{ fontSize: 12.5, color: W.body }}>Total</span>
        <span style={{ marginLeft: 'auto', fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {/* Matches what is actually listed above it. */}
          {isMobile ? '£67.00' : '£89.00'}
        </span>
      </div>
    </div>
  );
}

/* ── 6. Campaigns ──────────────────────────────────────────────────────── */

/**
 * A funnel, not another table.
 *
 * Campaigns, Money and Sponsors were all "title, four stat tiles, a list of
 * rows", which made three different parts of the product look like one
 * component with the words swapped. A campaign's shape IS a funnel: a number
 * sent, a smaller number opened, a smaller number again who entered. Drawing
 * that says what the surface is before anybody reads a word of it.
 */
const FUNNEL: Array<[string, string, number, string]> = [
  ['Sent', '3,412', 100, '#0C1312'],
  ['Opened', '2,081', 61, '#006D6D'],
  ['Clicked', '744', 22, '#4FA79F'],
  ['Entered', '31', 4, '#C08A2E'],
];

export function CampaignsSurface() {
  const isMobile = useIsMobile();
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Title sub="Early bird push · email and Instagram">Campaign performance</Title>
        <span style={{ marginLeft: 'auto', flex: 'none' }}>
          <Pill tone="good">Running</Pill>
        </span>
      </div>

      <div style={{ display: 'grid', gap: 11 }}>
        {FUNNEL.map(([label, value, pct, colour], k) => (
          <div key={label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12.5, color: W.body }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </div>
            <div style={{ height: 9, borderRadius: 5, background: '#EDF2F0', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%', width: `${pct}%`, borderRadius: 5, background: colour,
                  animation: `fvGrow 1.4s cubic-bezier(0.22,1,0.36,1) ${(k * 0.16).toFixed(2)}s both`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Attribution by channel. The card claims you can see which channel sold
          the entry, and the funnel alone does not show that. Dropped on a phone,
          where the funnel and the total are the argument and this pushed the
          card past what fits. */}
      {!isMobile && (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', color: W.mute, marginBottom: 6 }}>
          WHICH CHANNEL SOLD IT
        </div>
        {([
          ['Email to past entrants', '18', '£720'],
          ['Instagram ads', '9', '£360'],
          ['Google search', '4', '£160'],
        ] as Array<[string, string, string]>).map(([channel, entries, value], k) => (
          <Row key={channel} last={k === 2} k={k}>
            <Grow title={channel} note={`${entries} entries`} />
            <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</span>
          </Row>
        ))}
      </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto', paddingTop: 12, borderTop: RULE }}>
        <span style={{ fontSize: 12.5, color: W.body }}>Revenue from this campaign</span>
        <span style={{ marginLeft: 'auto', fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: W.teal }}>
          £1,240
        </span>
      </div>
    </div>
  );
}

/* ── 7. Money ──────────────────────────────────────────────────────────── */

const LEDGER = [
  { initials: 'MK', name: 'Maria Kelleher', note: "Women's doubles, 2 entries", state: 'Cleared', amount: '£80' },
  { initials: 'TB', name: 'Team Broadstone', note: 'Mixed doubles, 4 entries', state: 'Clearing', amount: '£160' },
  { initials: 'LN', name: 'Laura Nolan', note: 'Refunded in full', state: 'Refunded', amount: '−£40' },
  { initials: 'PO', name: 'Payout to your account', note: 'Weekly payout, Friday', state: 'Paid', amount: '£1,180' },
];

export function MoneySurface() {
  const isMobile = useIsMobile();
  const ledger = isMobile ? LEDGER.slice(0, 3) : LEDGER;
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <Title sub="£8,940 taken this season, £1,240 paying out Friday">Money</Title>

      <Stats items={[['Taken', '£8,940'], ['Fees', '£402'], ['Refunded', '£120'], ['Paying out', '£1,240']]} />

      <div style={{ minWidth: 0 }}>
        {ledger.map((t, i) => (
          <Row key={t.name} last={i === ledger.length - 1} k={i}>
            <Avatar initials={t.initials} />
            <Grow title={t.name} note={t.note} />
            <span style={{ flex: 'none' }}>
              <Pill tone={t.state === 'Cleared' || t.state === 'Paid' ? 'good' : t.state === 'Refunded' ? 'neutral' : 'warn'}>
                {t.state}
              </Pill>
            </span>
            <span style={{ flex: 'none', width: 52, textAlign: 'right', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {t.amount}
            </span>
          </Row>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: W.mute, marginTop: 'auto' }}>
        Viewing {ledger.length} of 318 transactions.
      </div>
    </div>
  );
}

/* ── 8. Sponsors and costs ─────────────────────────────────────────────── */

/**
 * A deliverables board, not another table.
 *
 * The claim on this card is that sponsors get what they paid for and that it
 * is tracked. A list of names and amounts shows neither; a card per sponsor
 * with its deliverables ticked off shows both, and stops this surface reading
 * as the money ledger with different nouns in it.
 */
const SPONSORS: Array<{ name: string; amount: string; paid: boolean; done: string[]; todo: string[] }> = [
  {
    name: 'Riverbank Physio',
    amount: '£1,500',
    paid: true,
    done: ['Finish line banner', 'Two social posts', 'Logo on bibs'],
    todo: [],
  },
  {
    name: 'Kelly’s Cycles',
    amount: '£750',
    paid: false,
    done: ['Race number branding'],
    todo: ['Recovery village stand'],
  },
];

export function SponsorsSurface() {
  const isMobile = useIsMobile();
  const sponsors = isMobile ? SPONSORS.slice(0, 1) : SPONSORS;
  return (
    <div style={surfaceStyle(isMobile)} data-fv-motion>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Title sub="£2,650 agreed, £1,150 still to collect">Sponsor deliverables</Title>
        <span style={{ marginLeft: 'auto', flex: 'none' }}>
          <Button primary>Add sponsor</Button>
        </span>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {sponsors.map((sp, k) => (
          <div
            key={sp.name}
            style={{
              border: RULE, borderRadius: 10, padding: '11px 12px',
              animation: `fvLine 620ms cubic-bezier(0.22,1,0.36,1) ${(k * 0.12).toFixed(2)}s both`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
              <span
                aria-hidden
                style={{
                  width: 26, height: 26, flex: 'none', borderRadius: 6,
                  background: k === 0 ? '#123B33' : '#3A2F5E',
                  color: '#fff', fontSize: 9.5, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {sp.name.slice(0, 2).toUpperCase()}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sp.name}
              </span>
              <Pill tone={sp.paid ? 'good' : 'warn'}>{sp.paid ? 'Paid' : 'Invoiced'}</Pill>
              <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{sp.amount}</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 12px' }}>
              {sp.done.map((d) => (
                <span key={d} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#1F6B54' }}>
                  <Check size={11} strokeWidth={2.6} style={{ flex: 'none' }} />
                  {d}
                </span>
              ))}
              {sp.todo.map((t) => (
                <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: W.mute }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, border: `1.5px solid ${W.borderStrong}`, flex: 'none' }} />
                  {t}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!isMobile && (
        <div style={{ fontSize: 11.5, color: W.mute, marginTop: 'auto' }}>
          Sponsor income lands in the same ledger as entries, so the day’s margin is one number.
        </div>
      )}
    </div>
  );
}

/** Keyed by the id in features.ts, so a card names its surface rather than importing one. */
export const SURFACES = {
  brandedPages: BrandedPagesSurface,
  splitPayment: SplitPaymentSurface,
  waves: WavesSurface,
  bracket: BracketSurface,
  formatPicker: FormatPickerSurface,
  checkIn: CheckInSurface,
  addOns: AddOnsSurface,
  campaigns: CampaignsSurface,
  money: MoneySurface,
  sponsors: SponsorsSurface,
} as const;

export type SurfaceId = keyof typeof SURFACES;
